import React, { useState } from "react";
import SidbarComponent from "./SidbarComponent";
import TopBarComponent from "./TopBarComponent";
import { FooterComponents } from "./FooterComponent";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { getToken } from "../utils/auth";
import { toast } from "react-toastify";
import { Pagination } from "flowbite-react";
import { Search, FileText, Loader2, Building2 } from "lucide-react";

export default function HliBursaryApplications() {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 10;

  const fetchApplications = async (page = 1) => {
    const res = await axios.get(
      process.env.REACT_APP_BASE_URL + "hec/busaryApplication/getByMyInstitution",
      {
        headers: { Authorization: "Bearer " + getToken() },
        params: { page: page - 1, size: itemsPerPage },
      }
    );
    setTotalPages(res.data.totalPages || 0);
    return res.data.content || [];
  };

  const {
    data: applications = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["hliBursaryApplications", currentPage],
    queryFn: () => fetchApplications(currentPage),
    onError: () =>
      toast.error("Failed to fetch bursary applications", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      }),
  });

  const filtered = applications.filter(
    (a) =>
      a.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.registrationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.indexNumber?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50 flex flex-col">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0"
        aria-label="Sidebar"
      >
        <SidbarComponent />
      </aside>

      <div className="p-6 sm:ml-64 transition-all duration-300 flex-1 flex flex-col">
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-cyan-100 rounded-xl">
                  <Building2 className="w-8 h-8 text-cyan-600" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    Bursary Applications
                  </h1>
                  <p className="text-gray-500 mt-1">
                    Applications from students in your institution
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, reg number or index..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <FileText className="w-12 h-12 mb-3" />
              <p className="text-lg font-medium">Failed to load applications</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <FileText className="w-12 h-12 mb-3" />
              <p className="text-lg font-medium">No applications found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                  <tr>
                    <th className="px-6 py-4">#</th>
                    <th className="px-6 py-4">Reg Number</th>
                    <th className="px-6 py-4">First Name</th>
                    <th className="px-6 py-4">Last Name</th>
                    <th className="px-6 py-4">Index Number</th>
                    <th className="px-6 py-4">College</th>
                    <th className="px-6 py-4">Education Level</th>
                    <th className="px-6 py-4">Academic Year</th>
                    <th className="px-6 py-4">Gender</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((app, i) => (
                    <tr
                      key={app.id}
                      className="hover:bg-cyan-50 transition-colors duration-150"
                    >
                      <td className="px-6 py-4 text-gray-500">
                        {(currentPage - 1) * itemsPerPage + i + 1}
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {app.registrationNumber || "-"}
                      </td>
                      <td className="px-6 py-4">{app.firstName || "-"}</td>
                      <td className="px-6 py-4">{app.lastName || "-"}</td>
                      <td className="px-6 py-4">{app.indexNumber || "-"}</td>
                      <td className="px-6 py-4">
                        {app.college?.collegeName || app.collegeName || "-"}
                      </td>
                      <td className="px-6 py-4">
                        {app.educationLevel || "-"}
                      </td>
                      <td className="px-6 py-4">
                        {app.academicYear?.academicYearCode || "-"}
                      </td>
                      <td className="px-6 py-4">{app.gender || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="flex justify-center py-4 border-t border-gray-100">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                    showIcons
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <FooterComponents />
    </div>
  );
}
